import React, { useState, useEffect } from 'react';
import { Modal } from './MainApp';
import {formatSauce, SAUCE_OPTIONS} from '../burgerOptions';

export default function BurgersTab({ selectedMember, fetchWithToken, token, API_URL }) {
    const [burgers, setBurgers] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [formData, setFormData] = useState({
        steaks: {
            cooking: 'point'
        },
        cheese: {
            count: 1,
            cooking: 'froid'
        },
        onions: [],
        tomato: false,
        lettuce: false,
        sauces: [],
        pickles: false,
        bacon: false
    });

    // Fetch tous les burgers
    useEffect(() => {
        if (!token) return;
        fetchWithToken(`${API_URL}/burgers`, token)
            .then(data => Array.isArray(data) ? setBurgers(data) : setBurgers([]));
    }, [token, API_URL]);

    // Trouver le burger du membre sélectionné
    const currentMemberBurger = selectedMember 
        ? burgers.find(burger => burger.member && burger.member._id === selectedMember._id)
        : null;

    const handleCreateBurger = () => {
        setIsEditing(false);
        setFormData({
            steaks: {cooking: 'point'},
            cheese: { count: 1, cooking: 'froid' },
            onions: [],
            tomato: false,
            lettuce: false,
            sauces: [],
            pickles: false,
            bacon: false,
            commentaire: '',
            status: 'a_faire'
        });
        setModalOpen(true);
    };

    const handleEditBurger = () => {
        if (!currentMemberBurger) return;
        
        setIsEditing(true);
        setFormData({
            steaks: {cooking: currentMemberBurger.steaks?.cooking || 'point'},
            cheese: currentMemberBurger.cheese,
            onions: currentMemberBurger.onions,
            tomato: currentMemberBurger.tomato,
            lettuce: currentMemberBurger.lettuce,
            sauces: currentMemberBurger.sauces,
            pickles: currentMemberBurger.pickles,
            bacon: currentMemberBurger.bacon,
            commentaire: currentMemberBurger.commentaire || ''
        });
        setModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (isEditing) {
                await fetchWithToken(`${API_URL}/burgers/${selectedMember._id}`, token, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                });
            } else {
                await fetchWithToken(`${API_URL}/burgers`, token, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        memberId: selectedMember._id,
                        ...formData
                    })
                });
            }
            
            setModalOpen(false);
            // Rafraîchir les données
            const updatedBurgers = await fetchWithToken(`${API_URL}/burgers`, token);
            if (Array.isArray(updatedBurgers)) {
                setBurgers(updatedBurgers);
            }
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
        }
    };

    const handleToggleArray = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].includes(value)
                ? prev[field].filter(item => item !== value)
                : [...prev[field], value]
        }));
    };

    const handleToggleBoolean = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const renderBurgerDetails = (burger) => {
        if (!burger) return null;

        const formatCooking = (cooking) => {
            const cookingMap = {
                'saignant': 'Saignant',
                'point': 'À point',
                'cuit': 'Cuit'
            };
            return cookingMap[cooking] || cooking;
        };

        const formatCheeseCooking = (cooking) => {
            return cooking === 'froid' ? 'Froid' : 'Cuit avec steak';
        };

        return (
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="text-2xl">🍔</span>
                    Burger de {burger.member?.pseudonym || 'Membre inconnu'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Côté gauche - Base du burger */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <span className="text-lg">🥩</span>
                            <span className="font-medium">Cuisson du steak:</span> {formatCooking(burger.steaks?.cooking || 'point')}
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <span className="text-lg">🧀</span>
                            <span className="font-medium">Cheddar:</span> {burger.cheese?.count || 1} ({formatCheeseCooking(burger.cheese?.cooking || 'froid')})
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <span className="text-lg">🧅</span>
                            <span className="font-medium">Oignons:</span>
                            {burger.onions && burger.onions.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                    {burger.onions.map(onion => (
                                        <span key={onion} className={`px-2 py-1 rounded text-xs ${
                                            onion === 'cru' ? 'bg-blue-100 text-blue-800' : 
                                            onion === 'caramelise' ? 'bg-orange-100 text-orange-800' : 
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {onion === 'cru' ? 'Cru' : onion === 'caramelise' ? 'Caramélisé' : 'Frit'}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-gray-500 text-xs">Sans oignons</span>
                            )}
                        </div>
                    </div>
                    
                    {/* Côté droit - Légumes, sauces et suppléments */}
                    <div className="space-y-3">
                        {/* Légumes */}
                        <div className="flex items-center gap-2">
                            <span className="text-lg">🥬</span>
                            <span className="font-medium">Légumes:</span>
                            <div className="flex gap-2">
                                {burger.tomato && <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">🍅 Tomate</span>}
                                {burger.lettuce && <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">🥬 Salade</span>}
                                {!burger.tomato && !burger.lettuce && <span className="text-gray-500 text-xs">Aucun</span>}
                            </div>
                        </div>
                        
                        {/* Sauces */}
                        <div className="flex items-start gap-2">
                            <span className="text-lg">🥫</span>
                            <div>
                                <span className="font-medium">Sauces:</span>
                                {burger.sauces && burger.sauces.length > 0 ? (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {burger.sauces.map(sauce => (
                                            <span key={sauce} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                                {formatSauce(sauce)}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-gray-500 text-xs ml-2">Aucune</span>
                                )}
                            </div>
                        </div>
                        
                        {/* Suppléments */}
                        <div className="flex items-start gap-2">
                            <span className="text-lg">➕</span>
                            <div>
                                <span className="font-medium">Suppléments:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {burger.pickles && <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">🥒 Pickles</span>}
                                    {burger.bacon && <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">🥓 Bacon</span>}
                                    {!burger.pickles && !burger.bacon && <span className="text-gray-500 text-xs">Aucun</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Commentaire */}
                {burger.commentaire && (
                    <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-start gap-2">
                            <span className="text-lg">💬</span>
                            <div>
                                <span className="font-medium text-gray-700">Commentaire pour le chef:</span>
                                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-blue-800 text-sm whitespace-pre-wrap">{burger.commentaire}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (!selectedMember) {
        return (
            <div className="text-center text-gray-400 text-lg py-20">
                Sélectionnez une personne pour faire la commande
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">
                    Commande de {selectedMember.pseudonym}
                </h2>
                
                {currentMemberBurger ? (
                    <button
                        onClick={handleEditBurger}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                        Modifier
                    </button>
                ) : (
                    <button
                        onClick={handleCreateBurger}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
                    >
                        Créer
                    </button>
                )}
            </div>

            {currentMemberBurger ? (
                renderBurgerDetails(currentMemberBurger)
            ) : (
                <div className="text-center text-gray-500 py-8">
                    Aucun burger créé pour {selectedMember.pseudonym}
                </div>
            )}

            {/* Modal de création/modification */}
            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <div className="w-full max-w-4xl mx-4 md:mx-0">
                    <div className="bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4">
                            <h2 className="text-xl font-semibold">
                                {isEditing ? 'Modifier' : 'Créer'} le burger de {selectedMember.pseudonym}
                            </h2>
                        </div>
                        
                        <div className="p-6">
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Base du burger */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Côté gauche - Base */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                        <span className="text-lg">🥩</span>
                                        Cuisson du steak
                                    </label>
                                    <div className="grid grid-cols-3 gap-3">
                                        {['saignant', 'point', 'cuit'].map(cooking => (
                                            <label key={cooking} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition min-h-[50px]">
                                                <input
                                                    type="radio"
                                                    name="steakCooking"
                                                    value={cooking}
                                                    checked={formData.steaks.cooking === cooking}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        steaks: {...prev.steaks, cooking: e.target.value}
                                                    }))}
                                                    className="mr-2"
                                                />
                                                <span className="font-medium text-sm">
                                                    {cooking === 'saignant' ? 'Saignant' : cooking === 'point' ? 'À point' : 'Cuit'}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Côté droit - Cheddar */}
                            <div className="space-y-4">
                                {/* Cheddar */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                        <span className="text-lg">🧀</span>
                                        Cheddar
                                    </label>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-2">Nombre</label>
                                            <div className="grid grid-cols-2 gap-3">
                                                {[1, 2].map(count => (
                                                    <label key={count} className="flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition min-h-[60px]">
                                                        <input
                                                            type="radio"
                                                            name="cheeseCount"
                                                            value={count}
                                                            checked={formData.cheese.count === count}
                                                            onChange={(e) => setFormData(prev => ({ 
                                                                ...prev, 
                                                                cheese: { ...prev.cheese, count: parseInt(e.target.value) }
                                                            }))}
                                                            className="mr-3"
                                                        />
                                                        <span className="font-medium text-base">{count}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-2">Mode de cuisson</label>
                                            <div className="grid grid-cols-2 gap-3">
                                                {['froid', 'cuit'].map(cooking => (
                                                    <label key={cooking} className="flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition min-h-[60px]">
                                                        <input
                                                            type="radio"
                                                            name="cheeseCooking"
                                                            value={cooking}
                                                            checked={formData.cheese.cooking === cooking}
                                                            onChange={(e) => setFormData(prev => ({ 
                                                                ...prev, 
                                                                cheese: { ...prev.cheese, cooking: e.target.value }
                                                            }))}
                                                            className="mr-3"
                                                        />
                                                        <span className="font-medium text-base">{cooking === 'froid' ? 'Froid' : 'Cuit avec steak'}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Oignons */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <span className="text-lg">🧅</span>
                                Oignons
                            </label>
                            <div className="space-y-3">
                                {['cru', 'caramelise', 'frit'].map(type => (
                                    <label key={type} className="flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition min-h-[60px]">
                                        <input
                                            type="checkbox"
                                            checked={formData.onions.includes(type)}
                                            onChange={() => handleToggleArray('onions', type)}
                                            className="mr-3"
                                        />
                                        <span className={`font-medium text-base px-3 py-1 rounded text-xs ${
                                            type === 'cru' ? 'bg-blue-100 text-blue-800' : 
                                            type === 'caramelise' ? 'bg-orange-100 text-orange-800' : 
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {type === 'cru' ? 'Cru' : type === 'caramelise' ? 'Caramélisé' : 'Frit'}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Légumes et Suppléments */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Légumes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                    <span className="text-lg">🥬</span>
                                    Légumes
                                </label>
                                <div className="space-y-3">
                                    <label className="flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition min-h-[60px]">
                                        <input
                                            type="checkbox"
                                            checked={formData.tomato}
                                            onChange={() => handleToggleBoolean('tomato')}
                                            className="mr-3"
                                        />
                                        <span className="font-medium text-base">🍅 Tomate</span>
                                    </label>
                                    <label className="flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition min-h-[60px]">
                                        <input
                                            type="checkbox"
                                            checked={formData.lettuce}
                                            onChange={() => handleToggleBoolean('lettuce')}
                                            className="mr-3"
                                        />
                                        <span className="font-medium text-base">🥬 Salade</span>
                                    </label>
                                </div>
                            </div>

                            {/* Suppléments */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                    <span className="text-lg">➕</span>
                                    Suppléments
                                </label>
                                <div className="space-y-3">
                                    <label className="flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition min-h-[60px]">
                                        <input
                                            type="checkbox"
                                            checked={formData.pickles}
                                            onChange={() => handleToggleBoolean('pickles')}
                                            className="mr-3"
                                        />
                                        <span className="font-medium text-base">🥒 Pickles</span>
                                    </label>
                                    <label className="flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition min-h-[60px]">
                                        <input
                                            type="checkbox"
                                            checked={formData.bacon}
                                            onChange={() => handleToggleBoolean('bacon')}
                                            className="mr-3"
                                        />
                                        <span className="font-medium text-base">🥓 Poitrine fumée</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Sauces */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <span className="text-lg">🥫</span>
                                Sauces
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                {SAUCE_OPTIONS.map(({value, label}) => (
                                    <label key={value} className="flex items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition min-h-[60px]">
                                        <input
                                            type="checkbox"
                                            checked={formData.sauces.includes(value)}
                                            onChange={() => handleToggleArray('sauces', value)}
                                            className="mr-3"
                                        />
                                        <span className="font-medium text-sm">{label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Commentaire */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <span className="text-lg">💬</span>
                                Commentaire pour le chef
                            </label>
                            <textarea
                                value={formData.commentaire}
                                onChange={(e) => setFormData(prev => ({ ...prev, commentaire: e.target.value }))}
                                placeholder="Instructions spéciales, préférences de cuisson, allergies, etc..."
                                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                rows="3"
                                maxLength="500"
                            />
                            <div className="text-right text-xs text-gray-500 mt-1">
                                {(formData.commentaire || '').length}/500 caractères
                            </div>
                        </div>

                        {/* Boutons */}
                        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t">
                            <button
                                type="button"
                                onClick={() => setModalOpen(false)}
                                className="w-full sm:w-auto px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                            >
                                {isEditing ? 'Modifier' : 'Créer'}
                            </button>
                        </div>
                    </form>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
